'use client';

import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';

interface PayoutHistoryItem {
  id: string;
  amount: number;
  date: string;
  status: 'completed' | 'pending';
}

interface PayoutHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  payouts: PayoutHistoryItem[];
}

const PayoutHistoryModal: React.FC<PayoutHistoryModalProps> = ({
  isOpen,
  onClose,
  isLoading,
  payouts,
}) => {
  const { t } = useTranslations();

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      classNames={{
        base: 'bg-white rounded-4',
        header: 'border-b border-tin pb-4',
        body: 'py-6',
        footer: 'border-t border-tin pt-4',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <Typography variant="h5" className="md:font-semibold">
            {t('partner.revshare.income.modal.historyTitle')}
          </Typography>
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="flex items-center justify-center h-[12.5rem]">
              <Spinner
                size="lg"
                classNames={{
                  circle1: '!border-b-partner',
                  circle2: '!border-b-partner/50',
                }}
              />
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[12.5rem] gap-2">
              <Typography variant="body1" className="text-auth-muted">
                {t('partner.revshare.income.modal.noPayouts')}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[25rem] overflow-y-auto">
              {payouts.map((payout) => (
                <div
                  key={payout.id}
                  className="flex items-center justify-between p-3 bg-gray-98 rounded-2"
                >
                  <div className="flex flex-col gap-1">
                    <Typography variant="body2" className="font-medium text-dark">
                      ${payout.amount.toFixed(2)}
                    </Typography>
                    <Typography variant="body3" className="text-auth-muted">
                      {formatDate(payout.date)}
                    </Typography>
                  </div>
                  <div
                    className={`px-2 py-1 rounded-1.5 text-xs font-medium ${
                      payout.status === 'completed'
                        ? 'bg-[rgba(23,201,100,0.2)] text-success'
                        : 'bg-[rgba(245,165,36,0.2)] text-warning'
                    }`}
                  >
                    {payout.status === 'completed'
                      ? t('partner.revshare.income.modal.statusCompleted')
                      : t('partner.revshare.income.modal.statusPending')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" variant="flat" onPress={onClose} className="rounded-2">
            {t('global.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default PayoutHistoryModal;
